import { Filter, X } from "lucide-react";
import axios from "axios";
import React, { useEffect } from "react";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useAllLists from "../../../hooks/use-all-lists";
import { useQuery } from "@tanstack/react-query";
import CommandBox from "../../ui/command-box";
import { useLanguage } from "../../../store/language-context";
import { useTranslation } from "react-i18next";

const FilterContent = ({ setSeeFilter, seeFilter, date, setDate, setSelected, selected, children }) => {
    const { t } = useTranslation();
    const { URL } = useLanguage();
    const {
        cities, cErr, cLoad,
        categories, catErr, catLoad,
        dependencies, dLoad, dErr,
    } = useAllLists({ includeWhat: ['cities', 'categories', 'dependencies'], perPage: 150 });

    const { data: subjects, error: sErr, isLoading: sLoad } = useQuery({
        queryKey: ['subjects-in-filter', selected?.category, URL],
        queryFn: async () => {
            if (!selected.category) return null;
            const URLSubject = `${URL}/subjects?category_id=${selected.category}`;
            const response = await axios.get(URLSubject);
            return response.data?.data?.data;
        },
    })

    const filteredCategories = categories?.filter((category) => category?.title !== "default" && category?.title !== "-");
    useEffect(() => {
        setSelected(prev => ({
            ...prev,
            subject: null, 
        }));
    }, [selected?.category, setSelected]); 

    return (
        <section className={`${seeFilter ? 'w-[24rem] max-lg:w-full ml-2' : 'w-0'}`}>
            <div className="bg-primary-tw w-full rounded-t-md flex justify-between items-center p-2">
                <div className="flex gap-2 items-center text-white">
                    <span>
                        <Filter size={24} color="white" fill="white" />
                    </span>
                    <span>
                        {t('training-courses.filter')}
                    </span>
                </div>
                <button
                    className="mx-2 closeIcon"
                    title="Close"
                    onClick={() => setSeeFilter(prev => !prev)}
                >
                    <X size={17} color="white" />
                </button>
            </div>

            <div className="p-4 space-y-4 border rounded-md !text-start">
                {/* اسم الدورة */}
                <div className="space-y-2">
                    <label className="block text-base text-semi-gray">{t('custom.course-name')}</label>
                    <div className="relative">
                        <input className="select-ui bg-gray-200"
                            value={selected?.title || ''}
                            placeholder={t('training-courses.search-course-name')}
                            onChange={(e) => setSelected(prev => ({
                                ...prev,
                                title: e.target.value
                            }))} />
                    </div>
                </div>

                {/* المدينه عشان البلد بتزعلهم */}
                <CommandBox
                    setSelected={setSelected}
                    selectedIsFor={selected?.city}
                    data={cities}
                    nameOfSelected={'city'}
                    label={t('training-courses.city')}
                    placeHolder={`${t('training-courses.search-for')} ${t('training-courses.city')}`}
                    error={cErr}
                    isLoading={cLoad}
                />

                {/* التاريخ */}
                <div className="space-y-2">
                    <label className="block text-base text-semi-gray rtl:!text-start !text-start">{t('training-courses.date')}</label>
                    <div className="flex gap-2 max-md:flex-col">
                        <div className="relative flex-1 date items-center flex">
                            <DatePicker
                                selected={date?.start ? new Date(date.start) : null}
                                onChange={(dateObj) => {
                                    const iso = dateObj.toISOString().split('T')[0];
                                    setDate(prev => ({ ...prev, start: iso }));
                                }}
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('training-courses.from')}
                                className="select-ui bg-gray-200 w-full"
                            />
                        </div>
                        <div className="relative flex-1 date items-center flex">
                            <DatePicker
                                selected={date?.end ? new Date(date.end) : null}
                                onChange={(dateObj) => {
                                    const iso = dateObj.toISOString().split('T')[0];
                                    setDate(prev => ({ ...prev, end: iso }));
                                }}
                                minDate={date?.start ? new Date(new Date(date.start).getTime() + 86400000) : null} // عشان اليوزر الغبي الاهطل التعبان في دماغو
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('training-courses.to')}
                                className="select-ui bg-gray-200 w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* موضوعات التدريب */}
                <CommandBox
                    selected={selected}
                    setSelected={setSelected}
                    selectedIsFor={selected?.category}
                    data={filteredCategories}
                    nameOfSelected={'category'}
                    label={t('training-courses.category')}
                    placeHolder={`${t('training-courses.search-for')} ${t('training-courses.category')}`}
                    error={catErr}
                    isLoading={catLoad}
                />

                <div className={`w-full ${selected?.category ? 'block' : 'hidden'}`}>
                    <CommandBox
                        setSelected={setSelected}
                        selectedIsFor={selected?.subject}
                        data={subjects}
                        nameOfSelected={'subject'}
                        label={t('training-courses.subject')}
                        placeHolder={`${t('training-courses.search-for')} ${t('training-courses.subject')}`}
                        error={sErr}
                        isLoading={sLoad}
                    />
                </div>

                {/* قائمه الاعتمادات */}
                <CommandBox
                    setSelected={setSelected}
                    selectedIsFor={selected?.dependence}
                    data={dependencies}
                    nameOfSelected={'dependence'}
                    label={t('training-courses.dependence')}
                    placeHolder={`${t('training-courses.search-for')} ${t('training-courses.dependence')}`}
                    error={dErr}
                    isLoading={dLoad}
                />

                {children}
            </div>
        </section>
    );
};

export default React.memo(FilterContent);
